import { normalizeState, parseDate } from '../../lib/championships.js'
import { submitLabelFor, GREEK_PURSUIT } from './categories.js'

/**
 * THE ADAPTER — one pursuit row → the normalized championship record shape.
 *
 * This is what lets a Command Center space read a completely different data
 * plane without a single view knowing. `GET /api/pursuits?line=<line>` returns
 * config-driven company or deal rows from sr-pursuit-tracker; every view in the
 * Command Center (PursuitBoard, GapsBoard, ManagerRollup, CrownMap, the filter
 * bar, the row selection, the CSV export) consumes the record shape produced by
 * `normalizeChampionship`. The adapter is the ONLY translation between them —
 * kept pure and React-free so `verify-spaces.mjs` can assert it directly.
 *
 * Deliberate holes, because inventing them would be a lie:
 *  - `effDate` is null UNLESS the row carries a real date of its own. A company
 *    pursuit has none, so it lands in NO DATE everywhere and the date-driven
 *    views are left out of that space (see `categories.js`). An RFP DOES have
 *    one — its CLOSE date, the submission deadline — and that becomes the
 *    effective date the countdown and every dated view key off. Nothing is
 *    derived, estimated or rolled forward: no close date on the row means no
 *    date on the record.
 *  - `sport` is blank unless the row actually carries one — which is what keeps
 *    a Police space from offering a football filter.
 *  - AN ORGANIZATION IS NOT AN EVENT. A corporate account runs an ongoing
 *    employee-recognition program: no sport, no event date, no champion and no
 *    incumbent ring vendor. `kind: 'employee-recognition'` marks the record so
 *    the shared views drop that framing entirely, and the dimensions that DO
 *    qualify one — headcount, industry, HQ, website, pursuit level — are mapped
 *    as first-class fields instead of squeezed into championship columns.
 *  - `value` is a real dollar amount only (a deal's `amount`). A company row's
 *    value BAND is a label, not a number, so it stays on `valueBand` and never
 *    gets a fabricated midpoint summed into TAM.
 *  - There is no champion, no crown and no `championStatus`: those belong to
 *    HubSpot 2-64425303, which a pursuit row has no record in.
 */

/**
 * The pursuit trackers speak their own status vocabulary per line. The shared
 * views (kanban columns, the status chip, the map's dominant fill) all key off
 * the championship `sr_pursuit_status` enum, so a row's status is mapped onto
 * the nearest canonical value — and the line's OWN label is preserved on
 * `stageLabel` so nothing is renamed on screen. An unrecognized status passes
 * through untouched rather than being forced into a bucket.
 */
export const CANONICAL_STATUS = {
  // company lines
  'not started': 'Not Started',
  'to pursue': 'To Pursue',
  // RFP / bid statuses (`sr_rfp_status`). An OPEN solicitation is one to
  // pursue; the line's own word ("Open") is kept on `stageLabel` and shown.
  open: 'To Pursue',
  'in review': 'Outreach In Progress',
  submitted: 'Outreach Completed',
  bid: 'Outreach Completed',
  awarded: 'Won',
  'outreach in progress': 'Outreach In Progress',
  'outreach completed': 'Outreach Completed',
  partnered: 'Won',
  won: 'Won',
  // deal stages
  surfaced: 'Not Started',
  qualified: 'To Pursue',
  'categorized & qualified': 'To Pursue',
  assigned: 'To Pursue',
  engaged: 'Outreach In Progress',
  'outreach/engaged': 'Outreach In Progress',
  proposal: 'Outreach Completed',
  'proposal/quote': 'Outreach Completed',
  verbal: 'Won',
  'verbal/won': 'Won',
  'closed won': 'Won',
}

export function canonicalStatus(raw) {
  const s = String(raw ?? '').trim()
  if (!s) return 'Not Started'
  return CANONICAL_STATUS[s.toLowerCase()] || s
}

/** value → label map for a config's `statusOptions` (strings or {value,label}). */
function statusLabels(config) {
  return Object.fromEntries(
    (config?.statusOptions || []).map((o) => (typeof o === 'string' ? [o, o] : [o.value, o.label]))
  )
}

function num(v) {
  if (v == null || v === '') return null
  const n = typeof v === 'number' ? v : Number(String(v).replace(/[^0-9.-]/g, ''))
  return Number.isFinite(n) ? n : null
}

function str(v) {
  if (v == null) return ''
  if (typeof v === 'object') return String(v.name ?? v.label ?? v.value ?? '').trim()
  return String(v).trim()
}

/* ---- The official source / submission URL --------------------------- */

/**
 * THE SUBMIT LINK. An RFP a rep can't actually bid on is useless, so every
 * pursuit row surfaces its official listing — the solicitation posting or the
 * submission portal — as a real link.
 *
 * The pursuit tracker's URL property name is not pinned down, so this resolves
 * it in two passes rather than guessing once:
 *
 *  1. `URL_FIELDS`, in priority order — a submission portal beats a listing
 *     page beats a bare `link`/`url`, and both camelCase and `sr_`-prefixed
 *     HubSpot spellings are covered.
 *  2. Failing that, ANY property whose NAME looks URL-ish (url / link / portal /
 *     solicit / bid / posting / rfp) and whose VALUE is an http(s) address.
 *
 * So a rename on the tracker side keeps working, and the field actually used is
 * reported back on `sourceUrlField` — the drawer shows it, so nobody has to
 * guess which property a link came from.
 *
 * Only http/https is ever accepted: a `javascript:` or `data:` value in a
 * record must never become a clickable link in the Command Center.
 */
export const URL_FIELDS = [
  'submissionUrl', 'submission_url', 'sr_submission_url',
  'portalUrl', 'portal_url', 'sr_portal_url',
  'solicitationUrl', 'solicitation_url', 'sr_solicitation_url',
  // `sr_rfp_source_url` is the property the RFP surfacing engine's spec
  // designates for the official BidNet/portal posting. Pinned here so the
  // designated field wins deterministically instead of relying on the
  // name-shaped fallback below and whatever order the record enumerates in.
  'sr_rfp_source_url', 'sr_rfp_portal_url',
  'sourceUrl', 'source_url', 'sr_source_url',
  'bidUrl', 'bid_url', 'sr_bid_url',
  'rfpUrl', 'rfp_url', 'sr_rfp_url',
  'postingUrl', 'posting_url', 'sr_posting_url',
  'link', 'sr_link', 'url', 'sr_url', 'website', 'sr_website',
]

const URLISH_KEY = /(url|link|portal|solicit|bid|posting|rfp)/i

/** '' unless the value is a real http(s) URL. */
export function safeUrl(v) {
  const s = str(v)
  if (!s) return ''
  if (!/^https?:\/\/[^\s]+$/i.test(s)) return ''
  try {
    const u = new URL(s)
    return u.protocol === 'http:' || u.protocol === 'https:' ? s : ''
  } catch {
    return ''
  }
}

/** `{ url, field }` for a row's official listing — `{ '', '' }` when it has none. */
export function pursuitUrl(row) {
  if (!row || typeof row !== 'object') return { url: '', field: '' }
  for (const f of URL_FIELDS) {
    const url = safeUrl(row[f])
    if (url) return { url, field: f }
  }
  for (const [k, v] of Object.entries(row)) {
    if (!URLISH_KEY.test(k)) continue
    const url = safeUrl(v)
    if (url) return { url, field: k }
  }
  return { url: '', field: '' }
}

/* ---- Dates + product category --------------------------------------- */

/**
 * THE CLOSE DATE. An RFP has no event date — it has a DEADLINE, and a bid you
 * find the day after it closes is worth nothing. The line's config names the
 * property (`dateProp`, e.g. `closeDate` for the RFP line); the list below is
 * the fallback for a feed that doesn't declare one, covering the camelCase,
 * snake_case and raw HubSpot spellings of the same idea.
 *
 * Nothing here derives or estimates a date the way `deriveSeasonDate` does for
 * a championship season: a row with no close date has NO date, full stop.
 */
export const CLOSE_DATE_FIELDS = [
  'closeDate', 'close_date', 'closedate',
  'dueDate', 'due_date', 'sr_rfp_due_date', 'sr_due_date',
  'deadline', 'submissionDeadline', 'submission_deadline',
]

/**
 * THE OPEN DATE — and the honest caveat that comes with it. The RFP engine does
 * not capture when the issuer opened a solicitation; what it records is
 * `sr_rfp_scan_date`, the day WE surfaced it. That is a proxy, so a record
 * sourced from a scan/created field is flagged `openDateIsProxy` and every
 * surface labels it "surfaced" rather than "opened".
 */
export const OPEN_DATE_FIELDS = [
  'openDate', 'open_date', 'postedDate', 'posted_date', 'sr_rfp_open_date',
  'scanDate', 'scan_date', 'sr_rfp_scan_date', 'createdate', 'createdAt',
]
const PROXY_OPEN_FIELD = /(scan|create)/i

/** What the pursuit is FOR — `sr_rfp_category` on the RFP line. */
export const PRODUCT_FIELDS = [
  'product', 'productCategory', 'product_category',
  'sr_rfp_category', 'sr_product_category', 'sr_category', 'category',
]

/* ---- Employee recognition (the organization spaces) ------------------ */

/**
 * A CORPORATION IS NOT A CHAMPIONSHIP. It has no sport, no event, no winner —
 * it runs an ongoing recognition program, and what sizes and qualifies one is
 * HEADCOUNT, INDUSTRY, WHERE HQ IS, how warm the pursuit is, and how to reach
 * them. Those five dimensions are mapped here, first-class on the record, so
 * the shared views can render, sort, filter and export them with no new
 * plumbing — and they stay blank in every space that doesn't record them, so
 * the controls disable themselves rather than offering an empty list.
 *
 * The line's config names each property (`employeesProp`, `industryProp`, …);
 * these lists are the fallback for a feed that doesn't declare one.
 */
export const EMPLOYEE_FIELDS = ['employees', 'numberofemployees', 'employeeCount', 'employee_count', 'sr_employees']
export const INDUSTRY_FIELDS = ['industry', 'sr_industry', 'sector', 'sr_sector']
export const HQ_FIELDS = ['hqLocation', 'hq_location', 'hq', 'sr_hq_location']
export const WEBSITE_FIELDS = ['website', 'sr_website', 'companyUrl', 'company_url', 'domain']
/** How warm the pursuit is — `hs_lead_status` on the corporate line. */
export const LEVEL_FIELDS = ['pursuitLevel', 'pursuit_level', 'hs_lead_status', 'sr_lead_status', 'sr_pursuit_level']

/* ---- Recognition + pursuit (the associated-deal spaces) -------------- */

/**
 * THE ASSOCIATED DEAL. Police / fire / greek records carry a deal that the
 * backend read alongside the company (`pursuits-hubspot.js` → `withAssociatedDeals`):
 *
 *  - `hero` on a police or fire department — its ONE Community Hero nomination.
 *  - `pursuitDeal` on a Greek organization — the product being pursued there.
 *
 * Both are carried through UNCHANGED as sub-objects (so `recognition.js` owns
 * the whole model and nothing re-derives a status here) and their date fields
 * are parsed with the same `parseDate` every other date in the Command Center
 * goes through. A record whose organization has no such deal gets `null`, which
 * is what every read surface renders as the honest "none yet" gap — never an
 * invented nomination and never a fabricated date.
 */
function adaptHero(raw) {
  if (!raw || typeof raw !== 'object') return null
  const years = num(raw.yearsOfService ?? raw.years_of_service ?? raw.sr_years_of_service)
  return {
    id: str(raw.id),
    dealname: str(raw.dealname),
    name: str(raw.heroName ?? raw.hero_name ?? raw.sr_hero_name),
    rank: str(raw.rank ?? raw.sr_hero_rank),
    status: str(raw.status ?? raw.stage ?? raw.dealstage),
    stage: str(raw.stage ?? raw.dealstage),
    owner: str(raw.owner),
    ownerId: str(raw.ownerId ?? raw.hubspot_owner_id),
    yearsOfService: years,
    nominatedAt: parseDate(raw.nominatedAt ?? raw.createdate ?? raw.created_at),
    presentedAt: parseDate(raw.presentedAt ?? raw.closedate),
  }
}

/**
 * @param {boolean} nameIsProduct  Does this line's deal NAME carry the product?
 *
 * ⚠️ TRUE ON GREEK ONLY, AND THAT IS THE WHOLE POINT.
 *
 * A Greek chapter opens one deal per product pursued, so the deal name really
 * is the product and the fallback is right. Every other line names its deal
 * canonically PER ORGANIZATION — `Cheer Partnership — <Gym>` — so the same
 * fallback mints one "product" per record. In the Cheer space that turned
 * "All products" into a 519-entry list of gym names; a filter with one option
 * per record cannot filter anything.
 *
 * Defaulting to FALSE is deliberate: a line added later gets the safe
 * behaviour (no product) rather than silently inheriting Greek's convention
 * and swamping its own filter bar.
 */
function adaptPursuitDeal(raw, nameIsProduct = false) {
  if (!raw || typeof raw !== 'object') return null
  return {
    id: str(raw.id),
    dealname: str(raw.dealname),
    // THE PRODUCT PURSUED. The explicit product field always wins; the deal
    // name is a fallback only where the line's convention actually supports it.
    product: str(raw.product ?? raw.sr_pursuit_product) || (nameIsProduct ? str(raw.dealname) : ''),
    status: str(raw.status ?? raw.stage ?? raw.dealstage),
    stage: str(raw.stage ?? raw.dealstage),
    owner: str(raw.owner),
    ownerId: str(raw.ownerId ?? raw.hubspot_owner_id),
    won: raw.won === true,
    lost: raw.lost === true,
    // WHEN THE PURSUIT OPENED and WHEN IT WAS WON. The won date is only ever
    // the deal's close date on a WON deal — a close date on a lost or open deal
    // is a forecast, and reporting a forecast as a win would be a lie.
    pursuitDate: parseDate(raw.pursuitDate ?? raw.pursuit_date ?? raw.createdate),
    wonDate: raw.won === true ? parseDate(raw.wonDate ?? raw.closedate) : null,
    amount: num(raw.amount),
  }
}

/**
 * THE CHEER PARTNERSHIP DEAL. Same associated-deal model as the hero nomination
 * and the Greek pursuit, carrying the two things that decide whether a gym is a
 * partner: whether the CONTRACT is signed (`won`, from HubSpot's own closed-won
 * flag) and where the EXCLUSIVE BRANDED JEWELRY PROGRAM has got to. A gym with
 * no partnership deal gets `null`, which every surface renders as TARGET — the
 * honest state, and the whole point of the space.
 */
/**
 * The partnership PROGRAM, or '' when the field only echoes the record.
 *
 * Compared on a normalized form so trivial spacing/case differences do not let
 * an echo through. Exported for the verify suite.
 */
export function partnershipProgram(raw) {
  const norm = (s) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ')
  const program = str(raw?.program ?? raw?.product ?? raw?.sr_partnership_program)
  if (!program) return ''
  return norm(program) === norm(raw?.dealname) ? '' : program
}

function adaptPartnershipDeal(raw) {
  if (!raw || typeof raw !== 'object') return null
  return {
    id: str(raw.id),
    dealname: str(raw.dealname),
    /* ⚠️ THE DEAL NAME IS *NOT* THE PRODUCT ON THIS LINE.
     *
     * That convention is Greek's, where a chapter opens one deal per product
     * pursued and the name genuinely is the product. A cheer partnership deal
     * is canonical PER GYM — `Cheer Partnership — <Gym>` — so falling back to
     * the name gave every gym its own "product" and the Cheer space's
     * "All products" filter became a 519-entry list of gym names:
     *
     *     360 All Stars — Cheer Partnership · 360Elite — Cheer Partnership
     *     5 Star Athletics — Cheer Partnership · … (519 entries)
     *
     * A filter with one option per record cannot filter anything; it just
     * swamps the bar. So the program is read from the program/product fields
     * only, and a partnership with none recorded reports NO product — which is
     * the true state today (`sr_jewelry_program` may not exist on the portal
     * yet) and drops the control rather than faking a dimension. */
    /* ⚠️ AND `program` IS THE DEAL NAME AGAIN, ONE LAYER DOWN.
     *
     * Measured on the live cheer line, 2026-09-11:
     *
     *     dealname: "Cheer Athletics — Cheer Partnership"
     *     program:  "Cheer Athletics — Cheer Partnership"   ← identical
     *     inferred: true
     *
     * The backend mirrors the deal name into `program` on an inferred deal, so
     * dropping the `dealname` fallback above was necessary but not sufficient —
     * 519 options became 301, all still gym names.
     *
     * A `program` that is byte-identical to the deal name carries no
     * information beyond the record's own identity, so it is not a product and
     * is read as absent. A real program ("Exclusive branded jewelry",
     * "Championship rings") is kept verbatim. Noted for the backend in
     * docs/BACKEND-HANDOVER.md §11.6 — this guard should become unnecessary. */
    product: partnershipProgram(raw),
    program: partnershipProgram(raw),
    status: str(raw.status ?? raw.stage ?? raw.dealstage),
    stage: str(raw.stage ?? raw.dealstage),
    owner: str(raw.owner),
    ownerId: str(raw.ownerId ?? raw.hubspot_owner_id),
    won: raw.won === true,
    lost: raw.lost === true,
    // WHERE THE JEWELRY PROGRAM HAS GOT TO, when the deal records it.
    jewelryStatus: str(raw.jewelryStatus ?? raw.sr_jewelry_status),
    pursuitDate: parseDate(raw.pursuitDate ?? raw.pursuit_date ?? raw.createdate),
    // A close date on an unsigned or lost deal is a forecast — the same refusal
    // the Greek line makes, for the same reason.
    wonDate: raw.won === true ? parseDate(raw.wonDate ?? raw.closedate) : null,
    amount: num(raw.amount),
  }
}

/**
 * THE EXCLUSIVE BRANDED JEWELRY PROGRAM — an attribute of the GYM, not a deal.
 * Read from whatever the row records; absent means "not launched", which is a
 * job rather than an unknown. `cheer.js` owns the interpretation.
 */
function adaptJewelry(row, deal) {
  const status = str(row?.jewelry?.status ?? row?.jewelryStatus ?? row?.sr_jewelry_program) ||
    str(deal?.jewelryStatus)
  const exclusive = row?.jewelry?.exclusive === true ||
    /^(true|yes)$/i.test(str(row?.jewelryExclusive ?? row?.sr_jewelry_exclusive)) ||
    /exclusive/i.test(status)
  if (!status && !exclusive) return null
  return { status, exclusive, launchedAt: parseDate(row?.jewelryLaunchedAt ?? row?.sr_jewelry_launched) }
}

/** First field in `fields` (config prop first) that carries a value → its key. */
function firstField(row, fields, prop) {
  const order = prop ? [prop, ...fields.filter((f) => f !== prop)] : fields
  for (const f of order) {
    const v = row?.[f]
    if (v != null && String(v).trim() !== '') return f
  }
  return ''
}

/** The first value among `fields` (config prop first), as a trimmed string. */
function pick(row, fields, prop) {
  const f = firstField(row, fields, prop)
  return f ? str(row[f]) : ''
}

export function adaptPursuitRow(row, ctx = {}) {
  const { config, owners = {}, category } = ctx
  const deal = config?.sourceType === 'deal'
  const nameProp = config?.nameProp || (deal ? 'dealname' : 'name')
  const statusProp = config?.statusProp || (deal ? 'dealstage' : 'sr_pursuit_status')
  const valueProp = config?.valueProp || (deal ? 'amount' : 'sr_value_band')
  const tierProp = config?.tierProp || 'sr_tier'
  const originProp = config?.originProp || 'sr_origin'
  const labels = statusLabels(config)

  // The status property the line declares, falling back to the mapped `status`
  // key and then the raw HubSpot one — a config that predates the mapping (or
  // an older cached response) still resolves a status instead of a blank.
  const rawStatus = row?.[statusProp] ?? row?.status ?? row?.sr_rfp_status
  const stageLabel = labels[rawStatus] || str(rawStatus)
  const pursuit = canonicalStatus(stageLabel || rawStatus)

  /* THE EFFECTIVE DATE — the close/deadline date when the line has one. */
  const closeField = firstField(row, CLOSE_DATE_FIELDS, config?.dateProp)
  const closeDate = closeField ? parseDate(row[closeField]) : null
  const openField = firstField(row, OPEN_DATE_FIELDS, config?.openDateProp)
  const openDate = openField ? parseDate(row[openField]) : null
  const productField = firstField(row, PRODUCT_FIELDS, config?.productProp)

  const name = str(row?.[nameProp]) || str(row?.name) || str(row?.dealname) || 'Untitled record'
  const geo = normalizeState({ sr_state: row?.state ?? row?.sr_state ?? '' })

  /* THE ASSOCIATED DEAL — the Community Hero nomination or the Greek pursuit. */
  const hero = adaptHero(row?.hero)
  /* Only the Greek line names a deal after the product it pursues — see
   * adaptPursuitDeal. Declared by the line's own config where it exists, so a
   * backend that starts sending `dealNameIsProduct` is honoured without an edit
   * here; otherwise it is the Greek space by kind. */
  const nameIsProduct = config?.dealNameIsProduct === true
    || (category?.kind || config?.kind) === GREEK_PURSUIT
  const pursuitDeal = adaptPursuitDeal(row?.pursuitDeal ?? row?.pursuit, nameIsProduct)
  const partnershipDeal = adaptPartnershipDeal(row?.partnershipDeal ?? row?.partnership)
  const jewelry = adaptJewelry(row, partnershipDeal)

  // THE OWNER. On a pursuit-tracked space the deal's owner is the person
  // actually working it, so it wins over the company's own owner — that is the
  // OWNER column Brian asked for. Everywhere else the company/record owner is
  // unchanged, so no existing space shifts.
  const owner =
    str(pursuitDeal?.owner) ||
    str(partnershipDeal?.owner) ||
    str(row?.sr_assigned_rep) ||
    (row?.hubspot_owner_id ? str(owners[row.hubspot_owner_id]) : '') ||
    str(row?.owner)

  const tier = str(row?.[tierProp])
  const valueBand = deal ? '' : str(row?.[valueProp])
  const amount = deal ? num(row?.[valueProp]) : null
  const source = pursuitUrl(row)

  /* EMPLOYEE RECOGNITION — the corporate dimensions (blank elsewhere). */
  const employeesField = firstField(row, EMPLOYEE_FIELDS, config?.employeesProp)
  const employees = employeesField ? num(row[employeesField]) : null
  const industry = pick(row, INDUSTRY_FIELDS, config?.industryProp)
  const city = str(row?.city)
  // The feed's own HQ string wins; failing that it is composed from the parts
  // that exist, so a city with no state never renders as "Austin, ".
  //
  // The fallback REQUIRES a city on purpose. A bare state is a jurisdiction,
  // not a headquarters — without this guard an RFP row (which carries the
  // solicitation's state and no city) would sprout an "HQ location: NC" column
  // it has no business having.
  const hqLocation =
    pick(row, HQ_FIELDS, config?.hqProp) ||
    (city ? [city, str(row?.state ?? row?.sr_state)].filter(Boolean).join(', ') : '')
  // Only ever an http(s) address — the same refusal that guards every other
  // link in the Command Center.
  const website = safeUrl(pick(row, WEBSITE_FIELDS, config?.websiteProp))
  const pursuitLevel = pick(row, LEVEL_FIELDS, config?.levelProp)
  // A corporation has no competitive "level"; what it has is how warm the
  // pursuit is. The shared level dimension carries whichever the line records,
  // and the label travels with it so no column is mis-titled.
  const level = deal ? '' : (tier || pursuitLevel)
  const levelLabel = config?.levelLabel || (!tier && pursuitLevel ? 'Pursuit level' : 'Level')

  return {
    // Namespaced so a pursuit id can never collide with a championship id in a
    // selection, an export, or a drawer target.
    id: `${category?.key || 'pursuit'}:${row?.id ?? name}`,
    name,

    // THE DATE. A company pursuit has none and says so. An RFP's close date is
    // a real, recorded deadline — never estimated, so it is always `confirmed`
    // and `dateEstimated` stays false.
    dateObj: closeDate,
    effDate: closeDate,
    dateConfidence: closeDate ? 'confirmed' : 'none',
    dateEstimated: false,
    seasonYear: null,
    dateRaw: closeField ? str(row[closeField]) : '',
    dateStatus: closeDate ? 'Confirmed' : 'N/A',
    dateLocked: false,

    // What that date IS, so the countdown reads "closes in 12d" and the column
    // header reads "Close date" instead of "Event date". A championship record
    // carries neither key, so its surface is untouched.
    dateKind: closeDate ? (config?.dateKind || category?.dateKind || 'close') : '',
    dateLabel: closeDate ? (config?.dateLabel || category?.dateLabel || 'Close date') : '',
    dateField: closeField,

    // THE OPEN DATE — and the flag that says it is a proxy (see above).
    openDate,
    openDateField: openField,
    openDateLabel: config?.openDateLabel || (PROXY_OPEN_FIELD.test(openField) ? 'Surfaced' : 'Opened'),
    openDateIsProxy: openDate
      ? (config?.openDateIsProxy ?? PROXY_OPEN_FIELD.test(openField))
      : false,

    // geo
    state: geo.code,
    stateSource: geo.source,
    stateRaw: geo.rawValue,

    // pursuit axis (canonical) + the line's own label, kept
    pursuit,
    stageLabel: stageLabel || pursuit,
    championStatus: '',
    crowned: false,
    workflowFired: false,
    readyForArtwork: false,
    ringFlag: null,

    // descriptors — the pursuit line's columns mapped onto the shared ones
    sport: str(row?.sr_sport || row?.sport),
    gender: '',
    level,
    levelLabel,
    division: str(row?.sr_division),
    conference: str(row?.sr_conference),
    season: '',
    segment: deal ? str(row?.[originProp]) : valueBand,
    city,
    region: str(row?.sr_region),

    // people
    rep: owner,
    leadRep: '',
    supportRep: '',

    // no winner concept on a pursuit line
    champion: '',
    winningSchool: '',
    crownedAt: null,

    /* ⚠️ THE COMPETITIVE INTEL, CARRIED THROUGH — and `incumbentSource` used to
     * be HARDCODED to '' right here, which silently overwrote a real value the
     * backend had just supplied.
     *
     * Writing this onto the HubSpot record was only half the job; the adapter
     * is the seam every client surface reads through, so a field it drops is a
     * field that does not exist as far as the board is concerned. The Work
     * Order scored every one of 519 gyms as "no signal yet" because of exactly
     * this, while 308 green unit assertions passed — they fed synthetic objects
     * that already had the right field names, so nothing tested the SEAM.
     *
     * `str()`/`num()` rather than a blank default: an absent property must read
     * as absent, never as a confident empty string. */
    incumbent: str(row?.incumbent || row?.sr_incumbent || row?.sr_current_vendor),
    incumbentConfidence: '',
    incumbentStatus: str(row?.incumbentStatus || row?.sr_incumbent_status),
    incumbentProducts: num(row?.incumbentProducts ?? row?.sr_incumbent_products),
    incumbentRingPrice: num(row?.incumbentRingPrice ?? row?.sr_incumbent_ring_price),
    incumbentLastSeen: str(row?.incumbentLastSeen || row?.sr_incumbent_last_seen),
    /* The as-of date. A competitor status with no date behind it is not one to
     * quote at a customer. */
    incumbentChecked: str(row?.incumbentChecked || row?.sr_incumbent_checked),
    incumbentSource: str(row?.incumbentSource || row?.sr_incumbent_source),

    value: amount,

    key: '',
    raw: row,

    // THE SUBMIT LINK — the official channel to participate. Carried on the
    // record (not threaded through props) so every surface that renders a
    // pursuit row — card, table, drawer — can offer it with no plumbing.
    sourceUrl: source.url,
    sourceUrlField: source.field,
    submitLabel: submitLabelFor(category),

    // WHAT THE PURSUIT IS FOR — the RFP's product category ("Awards &
    // Recognition", "Graduation Regalia", …). A first-class sortable and
    // filterable dimension in a pursuit space; blank everywhere else, so the
    // control disables itself rather than offering an empty list.
    //
    // ON THE GREEK LINE the product pursued IS the associated deal's name, so
    // it lands in this SAME field — which means the product column, the product
    // filter, the product sorter and the CSV's product column all light up for
    // Greek with no new plumbing anywhere.
    //
    // ON THE CHEER LINE the same slot carries what the PARTNERSHIP is for (the
    // exclusive branded jewelry program, championship rings, …), so the gym
    // board gets the product column, filter, sorter and CSV column for free.
    product: (productField ? str(row[productField]) : '') ||
      str(pursuitDeal?.product) || str(partnershipDeal?.product),
    productField: productField ||
      (pursuitDeal?.product ? 'pursuitDeal.product' : partnershipDeal?.product ? 'partnershipDeal.product' : ''),
    productLabel: config?.productLabel ||
      (pursuitDeal ? 'Product pursued' : partnershipDeal ? 'Partnership program' : 'Product category'),

    // WHAT KIND OF SPACE THIS RECORD BELONGS TO. `employee-recognition` is what
    // the shared views read to drop the championship framing (sport accent,
    // event date, countdown, champion, incumbent ring vendor) and render the
    // corporate column set instead. A championship record carries no `kind` at
    // all, so its surface is untouched.
    kind: config?.kind || category?.kind || '',

    // THE EMPLOYEE-RECOGNITION DIMENSIONS. A real headcount or null — never 0
    // for blank, because 0 sorts and reads as a real answer.
    employees,
    employeesField,
    employeesLabel: config?.employeesLabel || 'Total employees',
    industry,
    industryLabel: config?.industryLabel || 'Industry',
    hqLocation,
    hqLabel: config?.hqLabel || 'HQ location',
    website,
    websiteLabel: config?.websiteLabel || 'Website',
    pursuitLevel,

    /* ---- THE RECOGNITION / PURSUIT LAYER (police · fire · greek) ------
     *
     * All of it comes from a real associated DEAL and nothing is fabricated: a
     * department with no nomination has `hero: null` and every surface renders
     * "No hero nominated", a Greek org with no pursuit has `pursuitDeal: null`
     * and its product / pursuit-date / won-date columns read as gaps. See
     * `recognition.js` for the status model and the one-hero-per-precinct rule.
     */
    hero,
    // How many ACTIVE nominations the backend found on this organization. The
    // model is one; a second is a data problem the UI has to be able to say out
    // loud rather than silently render the first of.
    heroCount: num(row?.heroCount) ?? (hero ? 1 : 0),
    pursuitDeal,
    pursuitCount: num(row?.pursuitCount) ?? (pursuitDeal ? 1 : 0),

    /* ---- THE CHEER GYM PARTNERSHIP -----------------------------------
     *
     * The contract (an associated DEAL) and the exclusive branded jewelry
     * program (an attribute of the gym). A gym with neither is a TARGET — the
     * honest state and the whole TAM job. `cheer.js` decides the stage; nothing
     * is derived here.
     *
     * `gymTeamCount` is the gym's OWN recorded team count when the data has one.
     * It is NOT the number of teams the title rollup derived (that lands on
     * `teamCount`), so the space can say "10 teams on record, 3 with titles"
     * instead of conflating the two.
     */
    partnershipDeal,
    partnershipCount: num(row?.partnershipCount) ?? (partnershipDeal ? 1 : 0),
    jewelry,
    jewelryStatus: str(row?.jewelryStatus ?? row?.sr_jewelry_program) || str(partnershipDeal?.jewelryStatus),
    gymTeamCount: num(row?.teamCount ?? row?.teams ?? row?.sr_team_count),

    // Flattened onto the record so the shared comparator, the shared table and
    // the CSV can read them like any other column. The cheer partnership's own
    // dates land here too, so one gym reads its opened / signed dates through
    // exactly the columns the Greek line already established.
    pursuitDate: pursuitDeal?.pursuitDate || partnershipDeal?.pursuitDate || null,
    wonDate: pursuitDeal?.wonDate || partnershipDeal?.wonDate || null,

    // WHAT KIND OF BODY IT IS — "Police Department", "Greek Chapter", "National
    // Organization". Drives the nationals-above-chapters grouping in the Greek
    // space and is a plain column everywhere else that records it.
    orgType: str(row?.orgType || row?.sr_org_type),

    // pursuit-only extras, carried for the drawer and any future column set
    sourceType: deal ? 'deal' : 'company',
    schoolTeamCoverage: row?.schoolTeamCoverage || null,
    // THE RAW HubSpot id, un-namespaced. `id` above is prefixed with the space
    // key so it can't collide with a championship; a deal association needs the
    // real one, and composing it by stripping a prefix would be fragile.
    sourceId: str(row?.id),
    lineKey: category?.line || '',
    categoryKey: category?.key || '',
    tier,
    valueBand,
    origin: str(row?.[originProp]),
    hasRelationship: str(row?.sr_has_relationship),

    // RFP identity — the issuing agency, the solicitation number, the brand it
    // was surfaced for. Shown in the drawer; blank on lines that don't carry it.
    issuer: str(row?.issuer || row?.sr_rfp_issuer || row?.sr_rfp_agency),
    refNumber: str(row?.number || row?.sr_rfp_number),
    brand: str(row?.brand || row?.sr_rfp_brand),
  }
}

export function adaptPursuitRows(rows, ctx) {
  return (rows || []).map((r) => adaptPursuitRow(r, ctx)).filter(Boolean)
}
